import { useState, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Card } from '@safetyhub/ui';
import {
  formatDateToLocal,
  formatObligationRecurrence,
  isProcessSubjectKey,
  type LegalObligationAnnualPeriod,
  type LegalObligationAnnualRow,
  isEquipmentSubjectKey,
} from '@safetyhub/shared';
import { apiErrorMessage } from '../../lib/api';
import { getLegalObligationAnnualGrid, legalDocumentsQueryKeys } from '../../lib/legalDocuments';
import { planLegalObligationInstance } from '../../lib/legalObligationEvidence';
import { todayDateInputInTimezone } from '../../lib/localDate';
import { useToastStore } from '../../stores/toast';
import { fulfilledAtToIso } from './EvidenceModal';
import { isoToLocalDate } from './LastPerformedModal';
import PlanDateModal from './PlanDateModal';
import {
  UNSCHEDULED_REASON_LABEL,
  describeAnnualCell,
  exclusionReasonLabel,
  subjectExcludedText,
} from '../../lib/legalDocumentLabels';
import { AgencyOverlayNote, agencyDisplayTitle } from './AgencyOverlayNote';

const MONTHS = Array.from({ length: 12 }, (_, i) => i + 1);

/**
 * 한 현장의 **연간 격자**(월 12칸) — 스토리 L2(「어디가 비었는지 보인다」)·L26(행 없이 카탈로그에서 파생).
 *
 * 칸 하나 = 의무의 **기간 하나**다(서버 `deriveAnnualPeriods` — 월별 12칸 · 분기 3칸 묶음 · 반기 6칸 · 연간 12칸).
 * 그래서 칸은 `colSpan`으로 그 기간이 차지하는 달을 덮는다. 칸의 판정(상태 · 「기록 없음」 · 이번 기간 · 예정)은
 * `describeAnnualCell` 한 곳이다.
 *
 * 연도는 앞·뒤 버튼으로만 옮긴다(날짜 선택 UI가 아니라 해 단위 이동이다). 처음엔 `year`를 보내지 않아
 * 서버가 **현장 시간대**의 올해로 답하고, 이동은 그 응답의 `year`에서 시작한다.
 */
export default function AnnualGrid({
  companyId,
  siteId,
  canManage = false,
  timezone = 'Asia/Seoul',
}: {
  companyId: string;
  siteId: string;
  /** `obligation.manage` — 있으면 지금·앞으로의 칸에 「계획」 입구(②-c E8)를 둔다 */
  canManage?: boolean;
  /** 현장 유효 시간대(의무 목록 응답의 `timezone`) — 계획일 변환과 「오늘」 */
  timezone?: string;
}) {
  const [year, setYear] = useState<number | undefined>(undefined);
  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: legalDocumentsQueryKeys.annual(companyId, siteId, year),
    queryFn: () => getLegalObligationAnnualGrid(companyId, siteId, year),
    enabled: !!companyId && !!siteId,
  });
  const queryClient = useQueryClient();
  const { showToast } = useToastStore();
  const [planTarget, setPlanTarget] = useState<PlanTarget | null>(null);
  const [planError, setPlanError] = useState<string | null>(null);
  const planMutation = useMutation({
    mutationFn: ({ target, plannedOn }: { target: PlanTarget; plannedOn: string | null }) =>
      planLegalObligationInstance(companyId, siteId, target.obligationId, {
        // 넣을 때도 그 칸의 기간을 함께 보낸다 — 다른 기간에 든 날이면 서버가 400(「계획일이 그 기간 밖입니다」)으로 막는다
        plannedAt: plannedOn ? fulfilledAtToIso(plannedOn, timezone) : null,
        periodKey: target.period.periodKey,
        ...(target.subjectKey ? { subjectKey: target.subjectKey } : {}),
      }),
    onSuccess: async (result) => {
      setPlanTarget(null);
      showToast(result.plannedAt ? '계획일을 저장했습니다.' : '계획을 지웠습니다.', 'success');
      // 격자(○)와 의무 목록(지금 기간의 계획일)이 같은 인스턴스를 읽는다 — 그 현장의 법정 서류 질의를 함께 다시 읽는다
      await queryClient.invalidateQueries({ queryKey: ['legal-documents', companyId, 'sites', siteId] });
    },
    onError: (err) => setPlanError(apiErrorMessage(err) ?? '계획일을 저장하지 못했습니다.'),
  });
  const onPlan = canManage
    ? (target: PlanTarget) => {
        setPlanError(null);
        setPlanTarget(target);
      }
    : null;

  const shownYear = data?.year ?? year;

  return (
    <section aria-label="연간 보기">
      <div className="mb-3 flex items-center gap-2">
        <Button
          size="sm"
          variant="secondary"
          disabled={shownYear === undefined || isFetching}
          onClick={() => shownYear !== undefined && setYear(shownYear - 1)}
          aria-label="이전 해"
        >
          이전 해
        </Button>
        <span
          className="min-w-[4.5rem] text-center text-sm font-bold text-ink-900"
          aria-live="polite"
        >
          {shownYear !== undefined ? `${shownYear}년` : ''}
        </span>
        <Button
          size="sm"
          variant="secondary"
          disabled={shownYear === undefined || isFetching}
          onClick={() => shownYear !== undefined && setYear(shownYear + 1)}
          aria-label="다음 해"
        >
          다음 해
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12" role="status" aria-label="연간 보기 불러오는 중">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-brand-600" />
        </div>
      ) : error || !data ? (
        <div role="alert" className="rounded-card border border-rose-200 bg-rose-50 p-4">
          <p className="text-sm font-medium text-rose-700">
            {(error && apiErrorMessage(error)) ?? '연간 보기를 불러오지 못했습니다.'}
          </p>
        </div>
      ) : data.rows.length === 0 ? (
        <Card padding="none" radius="lg" elevation="md" className="py-12 text-center">
          <p className="text-sm font-medium text-ink-900">이 현장에 표시할 법정 서류가 없습니다</p>
        </Card>
      ) : (
        <Card padding="none" radius="lg" elevation="md" className="overflow-x-auto">
          <table className="min-w-[960px] table-fixed text-xs">
            <colgroup>
              <col className="w-56" />
              {MONTHS.map((m) => (
                <col key={m} />
              ))}
            </colgroup>
            <thead className="bg-ink-50 text-left font-bold text-ink-500">
              <tr>
                <th scope="col" className="px-4 py-3">
                  문서명
                </th>
                {MONTHS.map((m) => (
                  <th key={m} scope="col" className="px-1 py-3 text-center">
                    {m}월
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-200">
              {data.rows.map((row) => (
                <AnnualRow key={row.obligationId} row={row} year={data.year} onPlan={onPlan} />
              ))}
              {/* 대상별 하위 줄(②-c E4)은 `AnnualRow`가 자기 행 뒤에 그린다 — tbody 안에서 Fragment로 */}
            </tbody>
          </table>
        </Card>
      )}
      {planTarget && (
        <PlanDateModal
          key={`${planTarget.obligationId}|${planTarget.subjectKey}|${planTarget.period.periodKey}`}
          obligationTitle={planTarget.title}
          subjectLabel={planTarget.subjectLabel}
          year={planTarget.year}
          startMonth={planTarget.period.startMonth}
          endMonth={planTarget.period.endMonth}
          initialDate={planTarget.period.instance?.plannedAt ? isoToLocalDate(planTarget.period.instance.plannedAt, timezone) : null}
          defaultDate={defaultPlanDate(planTarget, timezone)}
          submitting={planMutation.isPending}
          errorMessage={planError}
          onSubmit={(plannedOn) => {
            setPlanError(null);
            planMutation.mutate({ target: planTarget, plannedOn });
          }}
          onClose={() => setPlanTarget(null)}
        />
      )}
    </section>
  );
}

/** 「계획」 입구가 연 칸 — 의무·대상·그 해·그 기간 */
interface PlanTarget {
  obligationId: string;
  /** 문서명 칸과 같은 이름(`agencyDisplayTitle`) */
  title: string;
  /** 대상 하위 줄이면 그 대상 키·이름 — 현장 단위면 `''`·생략 */
  subjectKey: string;
  subjectLabel?: string;
  year: number;
  period: LegalObligationAnnualPeriod;
}

type OnPlan = ((target: PlanTarget) => void) | null;

/** 계획이 없을 때의 초기값 — 지금 기간이면 오늘(현장 시간대), 앞으로의 기간이면 그 첫 달 1일 */
function defaultPlanDate(target: PlanTarget, timezone: string): string {
  if (target.period.timing === 'CURRENT') return todayDateInputInTimezone(timezone);
  return `${target.year}-${String(target.period.startMonth).padStart(2, '0')}-01`;
}

/**
 * 문서명 칸 — 대행 현장이면 **의무 목록 화면과 같은 표시**다(사용자 결정 2026-10-30): 기관 이름 + 「공통 서식 이름」 병기 ·
 * 「기관 서식에서 나눈 항목」 · 「기관 서식에서 쓰지 않음」(흐리게). 조각은 의무 목록과 같은 `AgencyOverlayNote`다.
 */
function AnnualRow({ row, year, onPlan }: { row: LegalObligationAnnualRow; year: number; onPlan: OnPlan }) {
  const title = agencyDisplayTitle(row);
  /** 칸 하나 → 계획 대상(행 · 대상 줄이 같은 조각) */
  const planFor = (subjectKey: string, subjectLabel?: string) =>
    onPlan
      ? (period: LegalObligationAnnualPeriod) =>
          onPlan({ obligationId: row.obligationId, title, subjectKey, subjectLabel, year, period })
      : null;
  const muted = !!row.exclusion || isOmittedByAgency(row);
  // ②-c E4 — 사람 대상 의무: 행 자체엔 칸이 없고 대상(사람)마다 하위 줄 하나. 제외·기관 미사용이면 하위 줄도 그리지 않는다
  const subjectRows = !muted && row.subjects.length > 0 ? row.subjects : [];
  return (
    <>
      <tr data-obligation-id={row.obligationId}>
        <th scope="row" className="px-4 py-2 text-left align-top font-normal">
          <p className={`text-sm font-bold ${muted ? 'text-ink-500' : 'text-ink-900'}`}>
            {agencyDisplayTitle(row)}
          </p>
          <AgencyOverlayNote row={row} />
          <p className="mt-0.5 text-ink-500">
            {formatObligationRecurrence(row) ?? '—'}
            {/* 설비 대상(②-c2)은 「대」 · 공정 × 유해인자 대상(②-c 2부)은 「개 대상」 · 사람은 「명」 — 대상 키 접두가 종류를 말한다 */}
            {subjectRows.length > 0
              ? ` · ${subjectRows.length}${
                  subjectRows.every((s) => isEquipmentSubjectKey(s.subjectKey))
                    ? '대'
                    : subjectRows.every((s) => isProcessSubjectKey(s.subjectKey))
                      ? '개 대상'
                      : '명'
                }`
              : ''}
          </p>
          {row.subjectNotice === 'MEASUREMENT_TARGET_REQUIRED' && !muted && (
            <p className="mt-0.5 text-xs text-amber-700" data-testid="measurement-target-notice">
              측정 대상(공정·유해인자) 등록 필요 — 등록하면 대상별 줄로 나뉩니다
            </p>
          )}
        </th>
        {row.subjectNotice === 'ROLE_ASSIGNMENT_REQUIRED' && !muted ? (
          <td colSpan={12} className="px-3 py-2 text-amber-700" data-testid="subject-notice">
            직위 배정 필요 — 조직 역할에서 법정 직위를 배정하면 대상별 칸이 생깁니다
          </td>
        ) : row.subjectNotice === 'EQUIPMENT_REVIEW_REQUIRED' && !muted ? (
          <td colSpan={12} className="px-3 py-2 text-amber-700" data-testid="subject-notice">
            설비 확인 필요 — 현장 상세 「대상」에서 설비 기종·규격 값을 넣거나 「설비 검토 완료」를 표시하세요
          </td>
        ) : subjectRows.length > 0 ? (
          <td colSpan={12} className="px-3 py-2 text-ink-400">
            대상별 칸은 아래 줄
          </td>
        ) : (
          <RowCells row={row} onPlanPeriod={planFor('')} />
        )}
      </tr>
      {subjectRows.map((subject) => (
        <tr key={subject.subjectKey} data-obligation-id={row.obligationId} data-subject-key={subject.subjectKey}>
          <th scope="row" className="px-4 py-1.5 pl-8 text-left align-top text-xs font-normal text-ink-700">
            {subject.label}
          </th>
          {subject.excludedReason ? (
            // 위탁(사용자 결정 2026-11-02 11:43 ⓑ) · 설비 판정 미완(②-c2) — 칸 없이 사유만. 의무 목록과 같은 문구(`subjectExcludedText`)
            <td colSpan={12} className="px-3 py-1.5 text-xs text-ink-500" data-testid="subject-excluded">
              {subjectExcludedText({ excludedReason: subject.excludedReason, pendingFieldLabels: subject.pendingFieldLabels })}
            </td>
          ) : (
            <PeriodCells
              periods={subject.periods}
              unscheduledReason={subject.unscheduledReason}
              onPlanPeriod={planFor(subject.subjectKey, subject.label)}
            />
          )}
        </tr>
      ))}
    </>
  );
}

/**
 * 행의 12칸 — 제외 · 칸 없음(사유) · 기간 칸들 중 하나. 기간 칸 사이·앞뒤의 빈 달(공사 기간이 그 해 일부만
 * 걸칠 때)은 빈 칸으로 채워 표가 어긋나지 않게 한다.
 */
function RowCells({
  row,
  onPlanPeriod,
}: {
  row: LegalObligationAnnualRow;
  onPlanPeriod: ((period: LegalObligationAnnualPeriod) => void) | null;
}) {
  if (row.exclusion) {
    return (
      <td colSpan={12} className="px-3 py-2 text-ink-500">
        제외됨 · {exclusionReasonLabel(row.exclusion.reasonCode)}
      </td>
    );
  }
  // 기관 서식이 쓰지 않는 항목 — 이행 기록이 만들어지지 않으므로(서버 실체화 층이 뺀다) 지난 칸을 「기록 없음」으로
  // 그리면 빠뜨린 것처럼 보인다. 1층 제외가 이기므로 그 뒤에 둔다.
  if (isOmittedByAgency(row)) {
    return (
      <td colSpan={12} className="px-3 py-2 text-ink-400">
        이행 기록을 만들지 않습니다
      </td>
    );
  }
  return <PeriodCells periods={row.periods} unscheduledReason={row.unscheduledReason} onPlanPeriod={onPlanPeriod} />;
}

/** 기간 칸들(행·대상 하위 줄이 같은 조각을 쓴다 — ②-c E4) */
function PeriodCells({
  periods,
  unscheduledReason,
  onPlanPeriod,
}: Pick<LegalObligationAnnualRow, 'periods' | 'unscheduledReason'> & {
  /** 「계획」 입구(②-c E8) — 권한이 없으면 null(칸은 ○ 표식만 그린다) */
  onPlanPeriod: ((period: LegalObligationAnnualPeriod) => void) | null;
}) {
  if (periods.length === 0) {
    return (
      <td colSpan={12} className="px-3 py-2 text-ink-500">
        {unscheduledReason
          ? UNSCHEDULED_REASON_LABEL[unscheduledReason]
          : '이 해에는 기간이 없습니다'}
      </td>
    );
  }

  const cells: ReactElement[] = [];
  let month = 1;
  for (const period of periods) {
    if (period.startMonth > month) {
      cells.push(<td key={`gap-${month}`} colSpan={period.startMonth - month} />);
    }
    cells.push(<PeriodCell key={period.periodKey} period={period} onPlan={onPlanPeriod} />);
    month = period.endMonth + 1;
  }
  if (month <= 12) cells.push(<td key={`gap-${month}`} colSpan={13 - month} />);
  return <>{cells}</>;
}

function isOmittedByAgency(row: LegalObligationAnnualRow): boolean {
  return row.agencyOverlay?.omittedByAgency === true;
}

/**
 * 칸 하나 — 상태 라벨 + 계획 표식(②-c E8 · 설계 2차 §7: ○ = `plannedAt`이 든 칸 · ● = 이행은 상태 라벨 「완료」가 든다).
 * 계획만 있고 기간이 끝났으면 상태 라벨이 「지연」이고 ○이 남는다 — 계획은 이행이 아니다.
 */
function PeriodCell({
  period,
  onPlan,
}: {
  period: LegalObligationAnnualPeriod;
  onPlan: ((period: LegalObligationAnnualPeriod) => void) | null;
}) {
  const { label, tone } = describeAnnualCell(period);
  const plannedAt = period.instance?.plannedAt ?? null;
  // 계획은 지금·앞으로의 기간에 적는다 — 지난 기간은 ○만 남긴다(지난 일의 계획을 고쳐도 이행이 바뀌지 않는다)
  const canPlan = !!onPlan && period.timing !== 'PAST';
  return (
    <td
      colSpan={period.endMonth - period.startMonth + 1}
      className="px-1 py-2 text-center"
      data-period-key={period.periodKey}
      data-timing={period.timing}
      data-planned-at={plannedAt ?? undefined}
    >
      {tone === 'muted' ? (
        <span className="text-ink-400">{label}</span>
      ) : (
        <Badge tone={tone} size="sm">
          {label}
        </Badge>
      )}
      {plannedAt && (
        <span className="ml-1 text-ink-500" title={`계획 ${formatDateToLocal(new Date(plannedAt))}`} aria-label="계획 있음">
          ○
        </span>
      )}
      {canPlan && (
        <button
          type="button"
          className="mt-0.5 block w-full text-[11px] font-semibold text-brand-600 underline decoration-dotted underline-offset-2"
          aria-label={plannedAt ? `${period.periodKey} 계획 변경` : `${period.periodKey} 계획`}
          onClick={() => onPlan?.(period)}
        >
          {plannedAt ? '계획 변경' : '계획'}
        </button>
      )}
    </td>
  );
}
